import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import FormEditor, { sameArrays } from './FormEditor';

const makeComponent = (getOptions) => {
  const Component = ({ value, onChange, editor, errors, disabled, data, ...rest }) => {
    const { id, name, label, property } = editor;
    const { minWidth, maxWidth, dense, required } = property;
    const handleChange = (event) => {
      onChange(name, event.target.value);
    };

    const labelId = `${id}-label`;
    const error = errors && errors.length && errors[0];
    let style;
    if (minWidth || maxWidth) {
      style = {
        minWidth: minWidth ? `${minWidth}px` : undefined,
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
      };
    }

    const options = getOptions(data, rest);
    const { displayed = true, ...userProps } = editor.componentProps(data) || {};

    return (
      <FormControl
        error={Boolean(error)}
        disabled={disabled}
        className={classnames(
          'grb-form-editor',
          'grb-form-editor-enum',
          !displayed && 'grb-form-editor-not-displayed',
        )}
        key={editor.id}
        style={style}
        required={required}
        {...userProps}
      >
        <InputLabel id={labelId}>{label}</InputLabel>
        <Select
          labelId={labelId}
          id={id}
          value={value}
          margin={dense ? 'dense' : undefined}
          onChange={handleChange}
        >
          {options.map((o) => (
            <MenuItem value={o.value} key={o.value}>{o.label}</MenuItem>
          ))}
        </Select>
        {Boolean(error) && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  };

  Component.propTypes = {
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({}),
      PropTypes.number,
    ]),
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
    editor: PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      id: PropTypes.string,
      property: PropTypes.shape({
        minWidth: PropTypes.number,
        maxWidth: PropTypes.number,
        dense: PropTypes.bool,
        required: PropTypes.bool,
      }),
      componentProps: PropTypes.func,
    }).isRequired,
    data: PropTypes.shape({}).isRequired,
    disabled: PropTypes.bool,
  };

  Component.defaultProps = {
    value: undefined,
    errors: undefined,
    disabled: undefined,
  };
  return Component;
};

class EnumFormEditor extends FormEditor {
  constructor(property, options) {
    super(property, options);
    const enumOptions = (options && options.options) || property.options;
    if (enumOptions) {
      this.getOptions = Array.isArray(enumOptions)
        ? () => enumOptions
        : (data, props) => enumOptions(data, props);
    }
    this.Component = makeComponent(this.getOptions);
    this.componentProps = options && options.props || (() => {});
  }

  computeValueState(value, currentState, data, props) {
    if (this.enumOptions) {
      const options = Array.isArray(this.enumOptions)
          ? this.options.options
          : this.options.options(data, props);
      if (!sameArrays(currentState.options, options)) {
        const newState = Object.assign({}, currentState, { options });
        if (!options.find(o => o.value === value)) {
          newState.value = options.length ? options[0].value : null;
        } else {
          newState.value = value;
        }
        return newState;
      }
    }
    return value === currentState.value ? null : { value };
  }

  ensureValue(value, data, props) {
    if (this.enumOptions) {
      const options = Array.isArray(this.enumOptions)
          ? this.enumOptions
          : this.enumOptions(data, props);
      if (!this.currentOptions || !sameArrays(this.currentOptions, options)) {
        this.currentOptions = options;
        if (!options.find(o => o.value === value)) {
          return options.length ? options[0].value : null;
        }
      }
    }
    return value;
  }
}

export default EnumFormEditor;
